import { FC, useState, useEffect } from "react"
import styled from "styled-components"
import { useRouter } from "next/router"
import { decode } from "shopify-gid"

import { useInView } from "react-intersection-observer"

import type { Document } from "@contentful/rich-text-types"
import {
  Color,
  ContentBlock,
  ContentfulProductWithShopifyData,
} from "@lib/types"

import {
  useDictionary,
  useFilter,
  useSort,
  useTracking,
  useSite,
} from "@hooks/index"

import { RichText } from "@components/common"
import { Container, BackLink, ModalWithImage } from "@components/ui"
import { PageSection } from "@components/page"
import {
  ProductSection,
  ProductGrid,
  CollectionFilters,
} from "@components/collection"

import { saveSession, SessionStorageKeys } from "@lib/utils"

const saveShopInUs = saveSession(SessionStorageKeys.SHOP_IN_US)

interface Props {
  page: {
    name: string
    title: string
    notAvailableInLocale: boolean | null
    category: {
      name: string
      slug: string
    }
    description: {
      json: Document
    }
    hideFilter: boolean
    productSectionsCollection: {
      alignStatic: string
      contentBlock: ContentBlock
      productsCollection: {
        items: ContentfulProductWithShopifyData[]
      }
    }[]
    filters: {
      color: Color[]
      fabricTexture: string[]
      fabricThickness: string[]
      size: string[]
    }
    pageSection: {
      __typename: string
      layout: string
    }
    products: ContentfulProductWithShopifyData[]
  }
}

export const CollectionPage: FC<Props> = ({ page }) => {
  const {
    title,
    notAvailableInLocale,
    category,
    description,
    productSectionsCollection,
    pageSection,
    hideFilter,
    products,
  } = page

  const site = useSite()
  const dictionary = useDictionary()
  const router = useRouter()
  const { track, tikTokTrack } = useTracking()
  const { locale } = useRouter();

  useEffect(() => {
    const products = productSectionsCollection
      .flatMap(({ productsCollection }) => [...productsCollection.items])
      .map((p, i) => ({
        name: `${p.title}${p.color ? ` - ${p?.color?.name}` : ''}`,
        product_id: p.shopifyProduct ? decode(p?.shopifyProduct?.id).id : null,
        position: i,
        category: p?.category?.name,
        url: `https://www.hautehijab.com/products/${p.slug}`,
      }))
    const contentsForTikTok = productSectionsCollection
      .flatMap(({ productsCollection }) => [...productsCollection.items])
      .map((p) => ({
          content_id: p.slug,
          content_type: 'product',
          content_name: `${p.title}${p.color ? ` - ${p?.color?.name}` : ''}`,
          quantity: 1,
        })
      )

    track("Product List Viewed", {
      list_id: `Collection - ${title}`,
      category: title,
      products,
    })

    tikTokTrack("ViewContent", {
      content_category: "Collection Page",
      content_name: `Collection - ${title}`,
      contents: contentsForTikTok
    })
  }, [])

  const {
    state,
    filteredProducts,
    handleChange: handleFilterChange,
    clearFilters,
  } = useFilter(products)

  const { isActive: filtersAreActive } = state

  const {
    sortIsActive,
    sortKey,
    handleChange: handleSortChange,
    sortOptions,
    sortBy,
  } = useSort()

  const sortedProducts = sortBy(filteredProducts)

  const isActive = filtersAreActive || sortIsActive

  useEffect(() => {
    if (isActive) {
      const filters = Object.entries(state.filters).flatMap((entry: any) =>
        entry[1].flatMap((value: string) => ({
          type: entry[0],
          value,
        }))
      )

      const products = sortedProducts.map((p, i) => ({
        name: `${p.title}${p.color ? ` - ${p?.color?.name}` : ''}`,
        position: i,
        category: p?.category?.name,
        url: `https://www.hautehijab.com/products/${p.slug}`,
      }))

      track("Product List Filtered", {
        list_id: `Collection - ${title}`,
        category: title,
        filters,
        sorts: [
          {
            type: "sort",
            value: sortKey,
          },
        ],
        products,
      })
    }
  }, [state, sortKey])

  const productCount = isActive
    ? sortedProducts.length
    : productSectionsCollection.reduce(
        (acc, val) => acc + val.productsCollection.items.length,
        0
      )

  const [sectionsToShow, setSectionsToShow] = useState<number>(2)
  const { ref, inView } = useInView()

  useEffect(() => {
    if (sectionsToShow < productSectionsCollection.length) {
      setSectionsToShow(sectionsToShow + 2)
    }
  }, [inView])

  const [modalOpen, setModalOpen] = useState(false)
  useEffect(() => {
    if (notAvailableInLocale) {
      setModalOpen(true)
    }
  }, [notAvailableInLocale])

  const handleBtnClick = () => {
    router.push('/')
    setModalOpen(false)
  }

  return (
    <StyledCollectionPage>
      {site?.collectionNotAvailableModal  && (
        <ModalWithImage
          modalOpen={modalOpen}
          closeModal={handleBtnClick}
          handleBtnClick={handleBtnClick}
          richText={site.collectionNotAvailableModal?.richText}
          button={site.collectionNotAvailableModal?.button}
          image={site.collectionNotAvailableModal?.image}
          imageMobile={site.collectionNotAvailableModal?.imageMobile}
        />
      )}
      <Header>
        <StyledContainer>
          <BackLink
            title={`${dictionary.all} ${category.name}`}
            href={`/${category?.slug}`}
          />
          <Introduction>
            <Title>{title}</Title>
            <Description>
              <RichText richText={description} />
            </Description>
          </Introduction>
        </StyledContainer>
        <StyledContainer>
          {!hideFilter && (
            <CollectionFilters
              filters={page.filters}
              filterState={state.filters}
              handleFilterChange={handleFilterChange}
              clearFilters={clearFilters}
              sortValue={sortKey}
              sortOptions={sortOptions}
              handleSortChange={handleSortChange}
              productCount={productCount}
            />
          )}
        </StyledContainer>
      </Header>
      <StyledContainer>
        <Collection>
          {isActive ? (
            <ProductGrid products={sortedProducts} />
          ) : (
            <>
              {productSectionsCollection
                .slice(0, sectionsToShow)
                .map((entry: any) => {
                  return <ProductSection key={entry.sys.id} entry={entry} />
                })}
            </>
          )}
        </Collection>
        <div ref={ref} />
      </StyledContainer>
      {pageSection && <PageSection data={pageSection} />}
    </StyledCollectionPage>
  )
}

const StyledCollectionPage = styled.div``

const StyledContainer = styled(Container)`
  margin: 0 0 4rem;
  padding: 0 1.6rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.md}px) {
    margin: 0 auto 2rem;
    max-width: 76.8rem;
  }
  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    max-width: 110rem;
  }
`

const Header = styled.div`
  padding: 3rem 0 12.1rem;
  background: ${({ theme }) => theme.color.beige};

  @media (min-width: ${({ theme }) => theme.breakpoint.xl}px) {
    padding: 3rem 0 19.6rem;
  }
`

const Introduction = styled.div`
  text-align: center;
  margin: 0 auto;
  padding: 0 2.5rem;
  max-width: 50rem;
  @media (min-width: ${({ theme }) => theme.breakpoint.xl}px) {
    padding: 0;
    max-width: 55rem;
  }
`

const Title = styled.h1`
  font-size: 3rem;
  line-height: 3.6rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`

const Description = styled.div`
  p {
    font-size: 1.5rem;
    line-height: 2.2rem;
    font-weight: 400;
  }
`

const Collection = styled.div`
  margin-top: -12rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.xl}px) {
    margin-top: -19.5rem;
  }
`
